/**
 * Factory dos DataSources.
 * -------------------------
 * UI consome via `useDataSources()` ou `getDataSources()`.
 * Nunca chamar Supabase direto da UI — ver regras invioláveis no CLAUDE.md.
 *
 * Quando uma integração externa estiver pronta, trocar a impl aqui
 * (ex: `instrumentacao: tetratechInstrumentacao` em vez de `manualInstrumentacao`).
 * A UI não muda — só esta linha.
 */

import { mockAi, anthropicEdgeAi, type AiDataSource } from './ai';
import { supabaseAuditoria, type AuditoriaDataSource } from './auditoria';
import { mockCameras, type CamerasDataSource } from './cameras';
import { supabaseContatos, type ContatosDataSource } from './contatos';
import { supabaseEstrutura, type EstruturaDataSource } from './estrutura';
import { computeOnReadEventos, type EventosDataSource } from './eventos';
import { manualInstrumentacao, type InstrumentacaoDataSource } from './instrumentacao';
import { manualPaebm, type PaebmDataSource } from './paebm';
import { supabaseShiftHandover, type ShiftHandoverDataSource } from './shift-handover';
import { stubNotificacoes, type NotificacoesDataSource } from './notificacoes';
import { mockSirenes, type SirenesDataSource } from './sirenes';
import { manualTarp, type TarpDataSource } from './tarp';
import { manualPlanos, type PlanosDataSource } from './planos';

export interface DataSources {
  instrumentacao: InstrumentacaoDataSource;
  cameras: CamerasDataSource;
  sirenes: SirenesDataSource;
  tarp: TarpDataSource;
  planos: PlanosDataSource;
  notificacoes: NotificacoesDataSource;
  paebm: PaebmDataSource;
  shiftHandover: ShiftHandoverDataSource;
  auditoria: AuditoriaDataSource;
  eventos: EventosDataSource;
  estrutura: EstruturaDataSource;
  contatos: ContatosDataSource;
  ai: AiDataSource;
}

// Modo atual: POC com inputs manuais.
// Para virar a chave de IA mock → Anthropic Edge: setar
// `VITE_USE_AI_EDGE=true` no .env e reiniciar dev server.
const useAiEdge = (import.meta.env.VITE_USE_AI_EDGE ?? 'false') === 'true';

export const dataSources: DataSources = {
  instrumentacao: manualInstrumentacao,
  cameras: mockCameras,
  sirenes: mockSirenes,
  tarp: manualTarp,
  planos: manualPlanos,
  notificacoes: stubNotificacoes,
  paebm: manualPaebm,
  shiftHandover: supabaseShiftHandover,
  auditoria: supabaseAuditoria,
  eventos: computeOnReadEventos,
  estrutura: supabaseEstrutura,
  contatos: supabaseContatos,
  ai: useAiEdge ? anthropicEdgeAi : mockAi,
};

/** Hook-friendly accessor. Use direto em components: `const { instrumentacao } = useDataSources()`. */
export function useDataSources(): DataSources {
  return dataSources;
}

// Re-exports tipados pra quem importa do barrel.
export type { AiDataSource } from './ai';
export type { AuditoriaDataSource, AuditFilter } from './auditoria';
export type { CamerasDataSource, Camera } from './cameras';
export type { EstruturaDataSource, DamOption } from './estrutura';
export { DAM_UNIDENTIFIED } from './estrutura';
export type {
  ContatosDataSource,
  ContatoEmergencia,
  ContatoGestao,
  ContatoSuporte,
  EdicaoContato,
  NovoContato,
  UsuarioDoApp,
} from './contatos';
export { ErroContato } from './contatos';
export type { EventosDataSource } from './eventos';
export type { InstrumentacaoDataSource } from './instrumentacao';
export type { PaebmDataSource } from './paebm';
export type { SirenesDataSource, Sirene, AcionamentoSirene } from './sirenes';
export type {
  TarpDataSource,
  TarpRecord,
  OpenTarpInput,
  CloseTarpInput,
  ListTarpsFilter,
  DraftPatch,
  AutoriaTarp,
} from './tarp';
export type { PlanosDataSource, CreatePlanoInput } from './planos';
export type { NotificacoesDataSource } from './notificacoes';
export type {
  ShiftHandoverDataSource,
  ShiftHandover,
  OperatorOption,
  CurrentUserRow,
  CreateHandoverInput,
  OpenShiftInput,
  ListHandoversFilter,
  TotpVerifyResult,
  TotpEnrollment,
} from './shift-handover';
export type { DataSourceMeta, DataSourceMode, PendingIntegration } from './types';
